import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  HStack,
  KdsButton,
  KdsCardFile,
  KdsCombobox,
  KdsModal,
  KdsText,
  TextArea,
  VStack,
} from "@kazaar-platform/design-system";
import { RiMessage2Line } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { OrderItemDeliveryLabelPatchCommandStatusEnum } from "@/config/apiClient";
import { useOrderItems, useToasts } from "@/hooks";
import { getDeliveryLabelStatusOptions } from "./ModalValidateDeliveryLabel.constants";
import {
  type InferredModalValidateDeliveryLabelSchema,
  modalValidateDeliveryLabelSchema,
} from "./ModalValidateDeliveryLabel.schema";
import { type ModalValidateDeliveryLabelProps } from "./ModalValidateDeliveryLabel.types";

const mapFormStatusToApiStatus = (
  formStatus: OrderItemDeliveryLabelPatchCommandStatusEnum
): OrderItemDeliveryLabelPatchCommandStatusEnum => {
  switch (formStatus) {
    case OrderItemDeliveryLabelPatchCommandStatusEnum.Validated:
      return OrderItemDeliveryLabelPatchCommandStatusEnum.Validated;
    case OrderItemDeliveryLabelPatchCommandStatusEnum.Refused:
      return OrderItemDeliveryLabelPatchCommandStatusEnum.Refused;
    default:
      return OrderItemDeliveryLabelPatchCommandStatusEnum.Validated;
  }
};

export const ModalValidateDeliveryLabel = ({
  isOpen,
  onOpenChange,
  addToast,
  orderItem,
}: ModalValidateDeliveryLabelProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();
  const { useValidateOrderItemDeliveryLabel } = useOrderItems();

  const [isCommentOpen, setIsCommentOpen] = useState(false);

  const DELIVERY_LABEL_STATUS_OPTIONS = getDeliveryLabelStatusOptions();

  const preferredAddToast = addToast || add;

  const { mutateAsync: validateOrderItemDeliveryLabel, isPending } =
    useValidateOrderItemDeliveryLabel();

  const methods = useForm<InferredModalValidateDeliveryLabelSchema>({
    resolver: zodResolver(modalValidateDeliveryLabelSchema),
    defaultValues: {
      deliveryLabelStatus:
        OrderItemDeliveryLabelPatchCommandStatusEnum.Validated,
    },
  });

  const onSubmit = async (data: InferredModalValidateDeliveryLabelSchema) => {
    try {
      await validateOrderItemDeliveryLabel({
        orderId: orderItem.orderId,
        orderItemId: orderItem.id,
        orderItemDeliveryLabelPatchCommand: {
          status: mapFormStatusToApiStatus(data.deliveryLabelStatus),
          ...(data.comment && { comment: data.comment }),
        },
      });

      preferredAddToast({
        state: "success",
        title: t("toast.generic.success"),
      });

      onOpenChange(false);
    } catch {
      preferredAddToast({
        state: "error",
        title: t("toast.generic.error"),
      });
    }
  };

  const onError = () => {
    preferredAddToast({
      state: "error",
      title: t("toast.generic.error"),
    });
  };

  return (
    <KdsModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="lg"
      dialogProps={{
        dialogHeaderProps: {
          title: t("modals.addDeliveryLabel.title"),
          description: t("modals.addDeliveryLabel.description"),
        },
        content: (
          <FormProvider {...methods}>
            <VStack gap="sp-md">
              <KdsCardFile
                fileName={orderItem.deliveryLabelFile?.fileName}
                fileUrl={orderItem.deliveryLabelFile?.url}
                fileSize={orderItem.deliveryLabelFile?.size}
                isViewable
              />

              <Box bgColor="elevation.second" p="sp-md" rounded="br-sm">
                <VStack gap="sp-md">
                  <HStack align="center" justify="space-between" gap="sp-md">
                    <Box grow={0} shrink={1} basis="100%">
                      <HStack gap="sp-xs" align="center">
                        <div className="whitespace-nowrap">
                          <KdsText size="subtitle-md">
                            {t("modals.validateDeliveryLabel.decisionLabel")}
                          </KdsText>
                        </div>

                        <Box maxW="75%" w="100%">
                          <Controller
                            name="deliveryLabelStatus"
                            control={methods.control}
                            render={({ field }) => {
                              const selectedOption =
                                DELIVERY_LABEL_STATUS_OPTIONS.find(
                                  (option) => option.id === field.value
                                );

                              return (
                                <KdsCombobox
                                  options={DELIVERY_LABEL_STATUS_OPTIONS.map(
                                    (option) => ({
                                      ...option,
                                      label: option.label,
                                      textValue: option.textValue,
                                    })
                                  )}
                                  onSelectionChange={field.onChange}
                                  selectedKey={field.value}
                                  inputValue={
                                    selectedOption ? selectedOption.label : ""
                                  }
                                />
                              );
                            }}
                          />
                        </Box>
                      </HStack>
                    </Box>

                    <KdsButton
                      variant="outlined"
                      color="neutral"
                      size="small"
                      iconLeft={RiMessage2Line}
                      onPress={() => setIsCommentOpen(!isCommentOpen)}
                    >
                      {t("modals.validateDeliveryLabel.displayCommentButton")}
                    </KdsButton>
                  </HStack>

                  {isCommentOpen && (
                    <Controller
                      name="comment"
                      control={methods.control}
                      render={({ field }) => (
                        <TextArea
                          {...field}
                          label={t("inputs.comment.label")}
                          placeholder={t("inputs.comment.placeholder")}
                          requiredLabel="optional"
                          maxLength={500}
                        />
                      )}
                    />
                  )}
                </VStack>
              </Box>
            </VStack>
          </FormProvider>
        ),
        dialogFooterProps: {
          buttonProps: [
            {
              children: t("modals.validateDeliveryLabel.cancelButton"),
              color: "neutral",
              variant: "ghost",
              isLoading: isPending,
              disabled: isPending,
              onPress: () => onOpenChange(false),
            },
            {
              children: t("modals.validateDeliveryLabel.validateButton"),
              type: "submit",
              isLoading: isPending,
              disabled: isPending,
              onPress: () => void methods.handleSubmit(onSubmit, onError)(),
            },
          ],
        },
      }}
    />
  );
};
